/*
 * Copyright (c) Nexign, JSC, 1992-2024.
 */
/**
 *
 *
 * @author Julia Ignatieva
 * @version 1.29.0
 * @since 1.29.0
 */

import React, { ChangeEvent, useCallback, useState } from 'react';

import { connect } from 'react-redux';
import type { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { icons } from '@/main/assets';
import { Input, LinkBlock } from '@components/common';
import { makeSelectTranslationWizardAdditionalReports } from '@modules/internationalization/intSelectors';
import { IWizardAdditionalReportsTranslation } from '@modules/internationalization/types/wizards/additionalReports';
import { IModalRenderProps } from '@modules/modal/types/modalTypes';
import ButtonServiceUserDenied from '@modules/role/components/ButtonServiceUserDenied';
import {
    ChangeSubscribersCheckedContainer, ChangeSubscribersCheckedElementContainer,
    ChangeSubscribersContainerStyled,
    ChangeSubscribersFooterStyled,
    ChangeSubscribersModalStyled,
    ChangeSubscribersSearchStyled, ChangeSubscribersTextStyled,
    ChangeSubscribersTitleStyled,
} from '@pages/WizardAdditionalReports/components/styled/ChangeSubscribersModalStyled';
import {
    WizardAdditionalReportsChangeSubscribersList,
} from '@pages/WizardAdditionalReports/components/WizardAdditionalReportsChangeSubscribersList';
import {
    IEditableId,
    TWizardAdditionalReportsSetParamsPayload,
} from '@pages/WizardAdditionalReports/types/wizardAdditionalReportsActionTypes';
import { wizardAdditionalReportsSetParamsAction } from '@pages/WizardAdditionalReports/wizardAdditionalReportsActions';
import { EWizardAdditionalReportsSteps } from '@pages/WizardAdditionalReports/wizardAdditionalReportsConsant';
import { IWizardAdditionalReportsReducer } from '@pages/WizardAdditionalReports/wizardAdditionalReportsReducer';
import {
    makeSelectWizardAdditionalReportsParam,
} from '@pages/WizardAdditionalReports/wizardAdditionalReportsSelectors';
import type { IRootReducer } from '@reducers/rootReducer';
import { stringFormatParams } from '@utils/stringFormat';

const WizardAdditionalReportsChangeSubscribersModal = ({
                                                           searchValue,
                                                           ids,
                                                           translation,

                                                           onCloseModal,
                                                           setParams,
                                                       }: IConnectedProps & IDispatchProps & IModalRenderProps) => {
    const modalTranslation = translation[EWizardAdditionalReportsSteps.PrepareStep].subscribersEditModal;
    const [updatedSubscribers] =
        useState<Map<number, IEditableId>>(new Map(ids.map(item => [item.id, item])));
    const [subscribersChosenCount, setSubscribersChosenCount] =
        useState<number>(ids.filter(item => item.checked).length);

    const onInputChange = useCallback((data: ChangeEvent<HTMLInputElement>) => {
            setParams({ searchValue: data.target.value });
        }, [setParams]);

    const onChooseElements = useCallback((isAllElements: boolean) => {
        updatedSubscribers.forEach(
            (item, id) => updatedSubscribers.set(id, { ...item, checked: isAllElements }));
        setSubscribersChosenCount(isAllElements ? ids.length : 0);
        setParams({ searchValue: '' });
    }, [updatedSubscribers, ids, setParams]);

    const onApply = () => {
        // @ts-ignore
        const newSubscribers: IEditableId[] = ids.map(item => updatedSubscribers.get(item.id));
        setParams({ ids: newSubscribers });
        onCloseModal();
    };

    const handleSwitchLabel = (subscriber: IEditableId) => {
        updatedSubscribers.set(subscriber.id, subscriber);
        setSubscribersChosenCount(Array.from(updatedSubscribers.values()).filter(item => item.checked).length);
    };

    return (
        <ChangeSubscribersContainerStyled
            data-component={'modal-popup'}
            data-name={'change-subscribers-modal'}>
            <ChangeSubscribersTitleStyled>
                {modalTranslation.title}
            </ChangeSubscribersTitleStyled>

            <ChangeSubscribersSearchStyled>
                <Input
                    autoFocus={true}
                    dataName='searchInput'
                    placeholder={modalTranslation.search}
                    righticon={icons.searchGreenIcon}
                    value={searchValue}
                    onChange={onInputChange}
                />
            </ChangeSubscribersSearchStyled>

            <ChangeSubscribersCheckedContainer>
                <ChangeSubscribersCheckedElementContainer>
                    {modalTranslation.choose}
                </ChangeSubscribersCheckedElementContainer>
                <ChangeSubscribersCheckedElementContainer>
                    <LinkBlock onClick={() => onChooseElements(true)}>
                        {modalTranslation.chooseAll}
                    </LinkBlock>
                </ChangeSubscribersCheckedElementContainer>
                <ChangeSubscribersCheckedElementContainer>
                    <LinkBlock onClick={() => onChooseElements(false)}>
                        {modalTranslation.chooseNone}
                    </LinkBlock>
                </ChangeSubscribersCheckedElementContainer>
            </ChangeSubscribersCheckedContainer>
            <ChangeSubscribersModalStyled>
                <WizardAdditionalReportsChangeSubscribersList
                    searchValue={searchValue}
                    subscribers={Array.from(updatedSubscribers.values())}
                    switchSubscriber={handleSwitchLabel}
                />
            </ChangeSubscribersModalStyled>

            <ChangeSubscribersTextStyled>
                {
                    stringFormatParams(
                        modalTranslation.chosenCount,
                        {
                            'count': subscribersChosenCount,
                            'all': ids.length,
                        },
                    )
                }
            </ChangeSubscribersTextStyled>

            <ChangeSubscribersFooterStyled>
                <ButtonServiceUserDenied
                    dataName='сhangeSubscribersButtonApply'
                    disabled={subscribersChosenCount === 0}
                    kind='mediumNormal'
                    onClick={onApply}
                >
                    {modalTranslation.apply}
                </ButtonServiceUserDenied>
                <LinkBlock
                    className='change-subscribers__footer-link'
                    onClick={onCloseModal}
                >
                    {modalTranslation.close}
                </LinkBlock>
            </ChangeSubscribersFooterStyled>
        </ChangeSubscribersContainerStyled>
    );
};

type TReducerProps = Pick<IWizardAdditionalReportsReducer,
    'searchValue' |
    'ids'
>;

interface IConnectedProps extends TReducerProps {
    searchValue: string;
    translation: IWizardAdditionalReportsTranslation;
}

export const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    searchValue: makeSelectWizardAdditionalReportsParam('searchValue'),
    ids: makeSelectWizardAdditionalReportsParam('ids'),
    translation: makeSelectTranslationWizardAdditionalReports(),
});

interface IDispatchProps {
    setParams: (payload: TWizardAdditionalReportsSetParamsPayload) => void;
}

export const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    setParams: payload => dispatch(wizardAdditionalReportsSetParamsAction(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(WizardAdditionalReportsChangeSubscribersModal);
